import { getSafeAreaPaddings, getTheme } from "@clipboard-health/ui-theme";
import { isDefined } from "@clipboard-health/util-ts";
import { CircularProgress, Stack, ThemeProvider } from "@mui/material";
// eslint-disable-next-line no-restricted-imports
import { type OnBoardingComponentProps } from "@src/app/onboarding/components/model";
import { OrientationScheduledConfirmation } from "@src/appV2/ClipboardOrientation/components/OrientationScheduledConfirmation";
import { isWorkerTimezoneMatchingDevice } from "@src/appV2/ClipboardOrientation/utils";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { IconButton } from "@src/appV2/redesign/components/IconButton";
import { PageWrapper } from "@src/appV2/redesign/components/PageWrapper";
import { useSkillsAssessmentFeatureFlags } from "@src/appV2/SkillsAssessment/api/useSkillsAssessmentFeatureFlags";
import { useSkillsAssessments } from "@src/appV2/SkillsAssessment/api/useSkillsAssessments";
import {
  type AssessmentEvent,
  type SkillsAssessmentStatus,
} from "@src/appV2/SkillsAssessment/types";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useHistory } from "react-router-dom";

export function ScheduledOrientationOnboardingConfirmation(props: OnBoardingComponentProps) {
  const { nextScreen, nextSection, previousScreen } = props;

  const worker = useDefinedWorker();
  const history = useHistory();

  useLogEffect(
    APP_V2_USER_EVENTS.CLIPBOARD_ORIENTATION_SCHEDULED_ONBOARDING_CONFIRMATION_PAGE_VIEWED
  );

  // we're no longer checking if the worker is defined in order to enable the call to the BE
  // because if it should always be set at this point, and if it is not, we will forward to
  // to the next sectionand the error will be handled by the next section
  const { skillsAssessmentConfig } = useSkillsAssessmentFeatureFlags();
  const {
    data: assessments,
    isLoading,
    isSuccess,
    refetch,
  } = useSkillsAssessments(
    {
      workerId: worker.userId,
      qualificationNames: skillsAssessmentConfig.availableQualificationNames,
      onFailure: () => {
        if (isDefined(nextSection)) {
          history.push(nextSection);
        }
      },
    },
    {
      staleTime: 0,
      cacheTime: 0,
    }
  );

  const latestScheduledEventId =
    assessments?.data?.[0]?.relationships.latestScheduledEvent.data?.id;
  const assessmentStatusId = assessments?.data?.[0]?.relationships.assessmentStatus.data?.id;

  const assessmentStatus = assessments?.included.find(
    (included) => included.type === "assessment-status" && included.id === assessmentStatusId
  ) as SkillsAssessmentStatus | undefined;
  const latestScheduledEvent = assessments?.included.find(
    (included) => included.type === "assessment-event" && included.id === latestScheduledEventId
  ) as AssessmentEvent | undefined;

  const scheduledFor = latestScheduledEvent?.attributes?.scheduledFor;

  const showTimezone = !isWorkerTimezoneMatchingDevice(worker.tmz);

  return (
    <ThemeProvider theme={getTheme()}>
      <PageWrapper
        sx={(theme) => ({
          ...getSafeAreaPaddings({
            top: true,
            bottom: true,
            fallbackBottom: theme.spacing(4),
          }),
          background: "#fff",
        })}
      >
        <Stack p={5} height="100%">
          <IconButton
            href={previousScreen}
            iconType="chevron-left"
            size="medium"
            variant="outlined"
            aria-label="Back"
          />

          <Stack alignItems="center" width="100%" pt={6} flex={1}>
            {isLoading && <CircularProgress />}
            {isSuccess && isDefined(assessmentStatus) && isDefined(scheduledFor) && (
              <OrientationScheduledConfirmation
                scheduledFor={scheduledFor}
                showTimezone={showTimezone}
                assessmentId={latestScheduledEvent?.attributes.assessmentId}
                onContinue={() => {
                  if (isDefined(nextScreen)) {
                    logEvent(
                      APP_V2_USER_EVENTS.CLIPBOARD_ORIENTATION_SCHEDULED_ONBOARDING_CONFIRMATION_BUTTON_CLICKED
                    );
                    history.push(nextScreen);
                  }

                  void refetch();
                }}
              />
            )}
          </Stack>
        </Stack>
      </PageWrapper>
    </ThemeProvider>
  );
}
